import React from "react";
import {
  Activity, AlertOctagon, Brain, ChevronRight, Clock, Lightbulb,
  RefreshCw, ShieldCheck, X,
} from "lucide-react";
import { Button } from "../ui/button";
import { fmtMoney, fmtPct, fmtNum } from "./_utils";

function KpiDrillDownModal({ drill, data, onClose, onNavigate }) {
  React.useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const kind = drill.kind;
  const isPreventable = kind === "preventable";
  const isRecoverable = kind === "recoverable";
  const isDriver = kind === "driver";
  const isPayor = kind === "payor";
  const isFirstPass = kind === "first_pass";
  const isAvgAging = kind === "avg_aging";

  let title = "";
  let subtitle = "";
  let headerIcon = null;
  if (isPreventable) {
    title = "Preventable Denials";
    subtitle = "Eligibility, authorization, coding & registration errors — fixable at the front-end.";
    headerIcon = <ShieldCheck className="h-5 w-5 text-teal-300" />;
  } else if (isRecoverable) {
    title = "Recoverable Revenue";
    subtitle = "High-probability appeal candidates with estimated collectable dollars.";
    headerIcon = <RefreshCw className="h-5 w-5 text-teal-300" />;
  } else if (isDriver) {
    title = drill.driver.driver;
    subtitle = "Denial driver detail — root cause, codes, payors, and recommended action.";
    headerIcon = <Brain className="h-5 w-5 text-teal-300" />;
  } else if (isPayor) {
    title = drill.payor.payor;
    subtitle = "Payor concentration detail — denial drivers, top codes, and recoverable opportunity.";
    headerIcon = <AlertOctagon className="h-5 w-5 text-teal-300" />;
  } else if (isFirstPass) {
    title = "First Pass Acceptance";
    subtitle = "Claims accepted on initial submission — the cleanest revenue cycle metric.";
    headerIcon = <Activity className="h-5 w-5 text-teal-300" />;
  } else if (isAvgAging) {
    title = "Average Days in AR";
    subtitle = "How long denied claims sit before resolution — the cost of inaction.";
    headerIcon = <Clock className="h-5 w-5 text-teal-300" />;
  }

  const k = data?.kpis || {};
  const drivers = data?.denial_drivers || [];
  const ansi = data?.top_ansi_codes || [];
  const outlook = data?.recovery_outlook || null;

  // Filter to the appropriate subset for each kind
  const preventableDrivers = drivers.filter((d) => (d.preventable_pct || 0) >= 25);
  const recoverableDrivers = drivers.slice().sort((a, b) => (b.recovery_opportunity || 0) - (a.recovery_opportunity || 0));
  const selectedDriver = isDriver ? drivers.find((d) => d.driver === drill.driver.driver) || drill.driver : null;
  const payorRows = data?.payors || [];
  const selectedPayor = isPayor ? payorRows.find((p) => p.payor === drill.payor.payor) || drill.payor : null;

  return (
    <div
      data-testid={`kpi-drill-down-${kind}`}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-950/75 backdrop-blur-sm p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-teal-400/30 shadow-[0_0_60px_-15px_rgba(20,184,166,0.45)]"
        style={{ background: "linear-gradient(160deg, #0B1524 0%, #122236 55%, #14283F 100%)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-slate-700/50 bg-slate-900/85 backdrop-blur-sm rounded-t-2xl">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(20,184,166,0.14)", border: "1px solid rgba(20,184,166,0.32)" }}>
              {headerIcon}
            </div>
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight">{title}</h2>
              <p className="text-sm text-slate-300 mt-0.5">{subtitle}</p>
            </div>
          </div>
          <button
            data-testid="kpi-drill-down-close"
            onClick={onClose}
            className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          {/* Hero stats — varies by drill kind */}
          {(isPreventable || isRecoverable) && (
            <div className="grid grid-cols-3 gap-3">
              {isPreventable ? (
                <>
                  <DrillStat label="Preventable Rate" value={fmtPct(k.preventable_pct)} footnote="of all denials" />
                  <DrillStat label="Volume" value={fmtNum(Math.round((k.preventable_pct / 100) * k.total_claims))} footnote="preventable claims" tone="teal" />
                  <DrillStat label="Industry Median" value={fmtPct(data?.benchmarks?.grades?.preventable_pct?.median ?? 27)} footnote="HFMA / MGMA benchmark" />
                </>
              ) : (
                <>
                  <DrillStat label="Expected Recovery" value={fmtMoney(outlook?.expected_recovery || 0)} footnote={`${outlook?.recovery_rate_pct || 0}% of ${fmtMoney(outlook?.total_denied || 0)}`} tone="teal" />
                  <DrillStat label="High-Confidence" value={fmtMoney(outlook?.high_confidence_recovery || 0)} footnote="Confidence ≥60%" tone="emerald" />
                  <DrillStat label="Appealable Accounts" value={fmtNum(outlook?.appealable_count || 0)} footnote={`${fmtMoney(outlook?.appealable_dollars || 0)} at stake`} />
                </>
              )}
            </div>
          )}

          {/* Driver-specific stats */}
          {isDriver && selectedDriver && (
            <div className="grid grid-cols-3 gap-3">
              <DrillStat label="Volume" value={fmtNum(selectedDriver.denial_count)} footnote={`${(selectedDriver.pct_of_total || 0).toFixed(1)}% of denials`} />
              <DrillStat label="$ at Risk" value={fmtMoney(selectedDriver.denial_dollars)} footnote="denied dollars" tone="teal" />
              <DrillStat label="Recovery Probability" value={fmtPct(selectedDriver.avg_recovery_likelihood || 0)} footnote={`${fmtMoney(selectedDriver.recovery_opportunity || 0)} recoverable`} tone="emerald" />
            </div>
          )}

          {/* Payor-specific stats */}
          {isPayor && selectedPayor && (() => {
            const share = k.total_denied_dollars ? (selectedPayor.dollars / k.total_denied_dollars) * 100 : 0;
            return (
              <div className="grid grid-cols-3 gap-3">
                <DrillStat label="Denied $" value={fmtMoney(selectedPayor.dollars)} footnote={`${share.toFixed(1)}% of total denials`} tone="teal" />
                <DrillStat label="Claim Volume" value={fmtNum(selectedPayor.volume)} footnote="denied claims" />
                <DrillStat label="Recoverable" value={fmtMoney(selectedPayor.recoverable_dollars || 0)} footnote="appealable dollars" tone="emerald" />
              </div>
            );
          })()}

          {/* First Pass Acceptance — hero stats + drivers of clean-claim leakage */}
          {isFirstPass && (() => {
            const fpMedian = data?.benchmarks?.grades?.first_pass_estimate?.median ?? 92;
            const fpDelta = (k.first_pass_estimate || 0) - fpMedian;
            const preventableClaims = Math.round(((k.preventable_pct || 0) / 100) * (k.total_claims || 0));
            return (
              <div className="grid grid-cols-3 gap-3">
                <DrillStat label="First Pass Rate" value={fmtPct(k.first_pass_estimate)} footnote={`${fpDelta >= 0 ? "+" : ""}${fpDelta.toFixed(1)} pp vs median`} tone={fpDelta >= 0 ? "emerald" : "teal"} />
                <DrillStat label="Industry Median" value={fmtPct(fpMedian)} footnote="HFMA / MGMA benchmark" />
                <DrillStat label="Preventable Volume" value={fmtNum(preventableClaims)} footnote="front-end fixable claims" />
              </div>
            );
          })()}

          {/* Average Days in AR — hero stats + aging distribution */}
          {isAvgAging && (() => {
            const arMedian = data?.benchmarks?.grades?.avg_aging_days?.median ?? 42;
            const arDelta = (k.avg_aging_days || 0) - arMedian;
            const aging = data?.aging || [];
            const over90 = aging
              .filter((b) => b.bucket === "91-120" || b.bucket === "121+")
              .reduce((acc, b) => acc + (b.dollars || 0), 0);
            return (
              <div className="grid grid-cols-3 gap-3">
                <DrillStat label="Avg Days in AR" value={`${k.avg_aging_days}d`} footnote={`${arDelta >= 0 ? "+" : ""}${arDelta.toFixed(1)}d vs median`} tone={arDelta <= 0 ? "emerald" : "teal"} />
                <DrillStat label="Industry Median" value={`${arMedian}d`} footnote="HFMA / MGMA benchmark" />
                <DrillStat label="$ Over 90 Days" value={fmtMoney(over90)} footnote="at high write-off risk" />
              </div>
            );
          })()}

          {/* First Pass — top preventable drivers (clean-claim leakage) */}
          {isFirstPass && (
            <div>
              <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-2.5">
                Top sources of clean-claim leakage
              </h3>
              <div className="space-y-2">
                {preventableDrivers.slice(0, 5).map((d) => (
                  <div key={d.driver} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-700/40 bg-slate-900/40">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white truncate">{d.driver}</div>
                      <div className="text-[11px] text-slate-300 mt-0.5">
                        {d.denial_count} claims · {d.preventable_pct}% preventable
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold tabular-nums text-teal-300">{fmtMoney(d.denial_dollars)}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">denied $</div>
                    </div>
                  </div>
                ))}
                {preventableDrivers.length === 0 && (
                  <div className="text-sm text-slate-400 italic px-3 py-4">No preventable drivers identified.</div>
                )}
              </div>
            </div>
          )}

          {/* Avg Aging — aging bucket distribution */}
          {isAvgAging && (data?.aging || []).length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-2.5">
                Aging distribution (denied $)
              </h3>
              <div className="space-y-2">
                {(() => {
                  const aging = data.aging || [];
                  const totalAging = aging.reduce((acc, b) => acc + (b.dollars || 0), 0) || 1;
                  const bucketTone = {
                    "0-30":   { color: "#86EFAC", label: "Fresh" },
                    "31-60":  { color: "#5EEAD4", label: "Healthy" },
                    "61-90":  { color: "#FCD34D", label: "Aging" },
                    "91-120": { color: "#FB923C", label: "At Risk" },
                    "121+":   { color: "#FCA5A5", label: "Critical" },
                  };
                  return aging.map((b) => {
                    const pct = (b.dollars / totalAging) * 100;
                    const tone = bucketTone[b.bucket] || { color: "#94A3B8", label: "" };
                    return (
                      <div key={b.bucket} className="rounded-lg border border-slate-700/40 bg-slate-900/40 p-3">
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white tabular-nums">{b.bucket} days</span>
                            <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: tone.color }}>{tone.label}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-semibold tabular-nums text-teal-300">{fmtMoney(b.dollars)}</span>
                            <span className="text-[11px] text-slate-400 ml-2 tabular-nums">{fmtNum(b.volume)} claims</span>
                          </div>
                        </div>
                        <div className="h-1.5 w-full rounded-full overflow-hidden bg-slate-800/80">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct.toFixed(1)}%`, background: tone.color }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Top contributing drivers — for preventable/recoverable */}
          {(isPreventable || isRecoverable) && (
            <div>
              <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-2.5">
                Top {isPreventable ? "preventable" : "recoverable"} drivers
              </h3>
              <div className="space-y-2">
                {(isPreventable ? preventableDrivers : recoverableDrivers).slice(0, 5).map((d) => (
                  <div key={d.driver} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-700/40 bg-slate-900/40">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white truncate">{d.driver}</div>
                      <div className="text-[11px] text-slate-300 mt-0.5">
                        {isPreventable
                          ? `${d.denial_count} claims · ${d.preventable_pct}% preventable`
                          : `${d.denial_count} claims · ${d.avg_recovery_likelihood}% avg recovery likelihood`}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold tabular-nums text-teal-300">
                        {fmtMoney(isPreventable ? d.denial_dollars : d.recovery_opportunity)}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {isPreventable ? "denied" : "expected recovery"}
                      </div>
                    </div>
                  </div>
                ))}
                {(isPreventable ? preventableDrivers : recoverableDrivers).length === 0 && (
                  <div className="text-sm text-slate-400 italic px-3 py-4">No data available.</div>
                )}
              </div>
            </div>
          )}

          {/* Driver-specific code list */}
          {isDriver && selectedDriver && (selectedDriver.top_carc || []).length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-2.5">
                Associated ANSI / CARC codes
              </h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {(selectedDriver.top_carc || []).slice(0, 6).map((c) => (
                  <div key={c.code} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-700/40 bg-slate-900/40">
                    <span className="inline-flex items-center font-mono font-bold text-[11px] px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-200 border border-teal-400/30">
                      CO-{c.code}
                    </span>
                    <span className="text-[12px] tabular-nums text-slate-300 shrink-0">{fmtNum(c.count)} claims</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top frequent ANSI codes — global for preventable/recoverable */}
          {(isPreventable || isRecoverable) && ansi.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-2.5">
                Most-frequent ANSI codes
              </h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {ansi.slice(0, 4).map((c) => (
                  <div key={c.code || c.display} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-700/40 bg-slate-900/40">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex items-center font-mono font-bold text-[11px] px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-200 border border-teal-400/30">
                        {c.code || c.display}
                      </span>
                      <span className="text-[12px] text-slate-200 truncate">{c.short_label || c.label || c.driver || "—"}</span>
                    </div>
                    <span className="text-[12px] tabular-nums text-slate-300 shrink-0">{fmtMoney(c.dollars || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payor — affected drivers */}
          {isPayor && (() => {
            const payorDrivers = (drivers || [])
              .map((d) => {
                const payorBreakdown = (d.top_payor || []).find((p) => p.payor === selectedPayor.payor);
                return payorBreakdown
                  ? { driver: d.driver, claims: payorBreakdown.count, recovery: d.avg_recovery_likelihood }
                  : null;
              })
              .filter(Boolean)
              .sort((a, b) => b.claims - a.claims)
              .slice(0, 5);
            if (payorDrivers.length === 0) return null;
            return (
              <div>
                <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 mb-2.5">
                  Top denial drivers for {selectedPayor.payor}
                </h3>
                <div className="space-y-2">
                  {payorDrivers.map((d) => (
                    <div key={d.driver} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-700/40 bg-slate-900/40">
                      <div className="text-sm font-semibold text-white">{d.driver}</div>
                      <div className="text-right">
                        <div className="text-sm tabular-nums text-teal-300">{fmtNum(d.claims)} claims</div>
                        <div className="text-[11px] text-slate-400">{d.recovery}% avg recovery</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* AI action callouts */}
          <div className="rounded-lg border border-teal-400/25 bg-teal-500/[0.08] p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="h-4 w-4 text-teal-300 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-teal-200">Recommendation</div>
                <p className="text-[13px] text-slate-200 mt-1 leading-relaxed">
                  {isPreventable && "Focus front-end workflow controls on Authorization, Eligibility and Coding — these three drivers account for the majority of preventable denials. Tightening intake QA + a pre-bill checklist typically reduces preventable denials by 25–40% within a quarter."}
                  {isRecoverable && "Prioritize Authorization and Medical Necessity appeal queues. Together they represent the largest pool of recoverable dollars with >65% historical resolution success. Use the AI Appeal Generator on the Work Queue to draft payor-ready letters in seconds."}
                  {isDriver && (selectedDriver?.recommended_action || "Open the Work Queue and use the AI Appeal Generator for high-probability resubmission letters.")}
                  {isPayor && `Open the ${selectedPayor?.payor} payor scorecard for benchmark variance and prioritize the top denial drivers above. Recovery rate for this payor is likely highest in Authorization and Medical Necessity buckets.`}
                  {isFirstPass && "First-pass leakage almost always traces back to front-end errors. Stand up a daily clean-claim QA huddle: verify eligibility at intake, validate authorization status before service, and run a pre-bill coder audit. Lifting first-pass acceptance by 3 points typically removes 10–15% of monthly denial volume."}
                  {isAvgAging && "Every additional day in AR compounds write-off risk. Triage the 91+ aging bucket first — these have the highest probability of being denied for timely filing if not appealed within 14 days. Use the Work Queue to prioritize accounts by recovery probability × aging."}
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              data-testid="kpi-drill-down-cta"
              onClick={() => onNavigate(isPayor ? "/scorecards" : "/work-queue")}
              className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold"
            >
              {isPayor ? "Open Payor Scorecards" : "Open Work Queue"}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrillStat({ label, value, footnote, tone = "slate" }) {
  const tones = {
    emerald: "border-emerald-400/40 bg-emerald-500/[0.10]",
    teal:    "border-teal-400/40 bg-teal-500/[0.10]",
    slate:   "border-slate-600/40 bg-slate-800/40",
  };
  return (
    <div className={`rounded-lg border ${tones[tone] || tones.slate} p-3.5`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-300">{label}</div>
      <div className="text-xl font-display font-semibold text-white mt-1 tabular-nums leading-none">{value}</div>
      <div className="text-[11px] text-slate-300 mt-1.5">{footnote}</div>
    </div>
  );
}

export { KpiDrillDownModal };
export default KpiDrillDownModal;
